import type { SmithersDeviceConfig } from "@/types/smithers-device"

export const SMITHERS_DEVICE_COLUMNS = [
  { key: "deviceId", label: "Device ID" },
  { key: "sotiDeviceId", label: "SOTI Device ID" },
  { key: "clientId", label: "Client ID" },
  { key: "validationVendor", label: "Validation Vendor" },
  { key: "facilityCode", label: "Facility Code" },
  { key: "venueId", label: "Venue ID" },
  { key: "areaCode", label: "Area Code" },
  { key: "deviceCode", label: "Device Code" },
  { key: "nteClubCode", label: "NTE Club Code" },
  { key: "matchThreshold", label: "Match Threshold" },
  { key: "validationTimeoutMs", label: "Validation Timeout (ms)" },
  { key: "proVenueUser", label: "ProVenue Instance" },
  { key: "proVenueInstances", label: "ProVenue Host" },
  { key: "createdAt", label: "Created At" },
  { key: "updatedAt", label: "Last Updated" },
  { key: "updatedBy", label: "Updated By" },
  { key: "id", label: "Record ID" },
] as const satisfies readonly { key: keyof SmithersDeviceConfig; label: string }[]

export type SmithersDeviceColumn = typeof SMITHERS_DEVICE_COLUMNS[number]["key"]
export const SMITHERS_OPTIONAL_COLUMNS = SMITHERS_DEVICE_COLUMNS.filter(({ key }) => key !== "deviceId" && key !== "clientId")

export const SMITHERS_DEVICE_COLUMNS_STORAGE_KEY = "smithers-device-columns:v1"
const defaultVisibleColumns = SMITHERS_OPTIONAL_COLUMNS.filter(({ key }) => key !== "createdAt" && key !== "updatedAt" && key !== "updatedBy" && key !== "id").map(({ key }) => key)

export function loadSmithersDeviceColumns(): Set<SmithersDeviceColumn> {
  try {
    const saved: unknown = JSON.parse(window.localStorage.getItem(SMITHERS_DEVICE_COLUMNS_STORAGE_KEY) ?? "null")
    if (Array.isArray(saved) && saved.every((key) => typeof key === "string")) {
      return new Set(SMITHERS_OPTIONAL_COLUMNS.filter(({ key }) => saved.includes(key)).map(({ key }) => key))
    }
  } catch {
    // Use the defaults when storage is unavailable or the saved value is invalid.
  }
  return new Set(defaultVisibleColumns)
}

export function saveSmithersDeviceColumns(columns: ReadonlySet<SmithersDeviceColumn>): void {
  try {
    const keys = SMITHERS_OPTIONAL_COLUMNS.filter(({ key }) => columns.has(key)).map(({ key }) => key)
    window.localStorage.setItem(SMITHERS_DEVICE_COLUMNS_STORAGE_KEY, JSON.stringify(keys))
  } catch {
    // Column toggling should still work when browser storage is unavailable.
  }
}

function sortValue(device: SmithersDeviceConfig, column: SmithersDeviceColumn): string | number {
  if (column === "proVenueInstances") return device.proVenueInstances[0]?.hostName ?? ""
  if (column === "proVenueUser") return device.proVenueInstanceId === null ? "None" : device.proVenueUser
  if (column === "createdAt" || column === "updatedAt") return Date.parse(device[column]) || 0
  return device[column]
}

export function compareSmithersDevices(a: SmithersDeviceConfig, b: SmithersDeviceConfig, column: SmithersDeviceColumn): number {
  const left = sortValue(a, column)
  const right = sortValue(b, column)
  return typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true })
}
